import { useEffect, useMemo, useSyncExternalStore } from 'react';

import type { HomeworkStore } from '../stores/homework-store';
import type { TeachingStore } from '../stores/teaching-store';
import {
  TEACHER_HOMEWORK_STATUSES,
  teacherHomeworkListStatus,
} from '../domain/homework-list-presentation';
import { HomeworkListPanel, type HomeworkListRow } from './HomeworkListPanel';

export function HomeworkReviewListPanel({
  homework,
  teaching,
  onCancel,
  onOpen,
}: {
  readonly homework: HomeworkStore;
  readonly teaching: TeachingStore;
  readonly onCancel: () => void;
  readonly onOpen: (homeworkId: string) => void;
}) {
  const homeworkState = useSyncExternalStore(
    homework.subscribe,
    homework.snapshot,
    homework.snapshot,
  );
  const teachingState = useSyncExternalStore(
    teaching.subscribe,
    teaching.snapshot,
    teaching.snapshot,
  );
  const classNames = useMemo(
    () =>
      new Map(
        teachingState.classes.map(
          (managedClass) => [managedClass.classId, managedClass.name] as const,
        ),
      ),
    [teachingState.classes],
  );

  useEffect(() => {
    let active = true;
    void teaching
      .loadTeacherDashboard()
      .then(() => {
        if (!active) return;
        const nextClassIds = teaching
          .snapshot()
          .classes.map((managedClass) => managedClass.classId);
        return homework.loadTeacherHomeworkOverview(nextClassIds);
      })
      .catch(() => {
        if (!active) return;
        if (teaching.snapshot().error !== null) {
          void homework.loadTeacherHomeworkOverview([]).catch(() => undefined);
        }
      });
    return () => {
      active = false;
    };
  }, [homework, teaching]);

  const loading =
    teachingState.pending === 'load-teacher' ||
    homeworkState.pending === 'load-homework-review-overview';

  const rows: HomeworkListRow[] = homeworkState.homeworks.map((item) => {
    const progress = homeworkState.reviewProgressByHomework[item.homeworkId];
    return {
      id: item.homeworkId,
      title: item.title,
      classId: item.classId,
      className: classNames.get(item.classId) ?? '班级信息未加载',
      date: item.publishedAt,
      dueAt: item.dueAt,
      metadata: `${item.items.length} 题 · ${item.answerMode === 'MANUAL' ? '手动作答' : '自动作答'}`,
      status: teacherHomeworkListStatus(progress),
      lifecycle:
        item.status === 'CLOSED' ? '已结束' : item.status === 'ARCHIVED' ? '已归档' : undefined,
      progress: progress
        ? {
            label: `已批改 ${progress.reviewedAssignments} / ${progress.totalAssignments} 名学生`,
            value: progress.reviewedAssignments,
            total: progress.totalAssignments,
          }
        : null,
      action: progress?.fullyGraded ? '查看批改' : '进入批改',
    };
  });
  const errors = [teachingState.error, homeworkState.error].filter((error) => error !== null);
  return (
    <HomeworkListPanel
      rows={rows}
      statuses={TEACHER_HOMEWORK_STATUSES}
      dateLabel="布置日期"
      description="查看负责班级的作业，跟进每份作业的批改进度。"
      emptyTitle="暂时没有已布置的作业"
      loading={loading}
      busy={loading || homeworkState.pending !== null}
      classOptions={teachingState.classes.map((item) => ({ id: item.classId, name: item.name }))}
      error={
        <>
          {homeworkState.notice ? (
            <p className="message" role="status">
              {homeworkState.notice}
            </p>
          ) : null}
          {errors.map((error, index) => (
            <p key={index} className="message message--error" role="alert">
              {error}
            </p>
          ))}
        </>
      }
      onOpen={onOpen}
      onDelete={(id) => {
        const target = homeworkState.homeworks.find((item) => item.homeworkId === id);
        if (
          target &&
          window.confirm(
            `确认删除作业“${target.title}”？\n删除后，该作业将从所有学生的作业和批改结果中移除，学生无法继续作答。`,
          )
        ) {
          void homework.deleteHomework(target).catch(() => undefined);
        }
      }}
      onCancel={onCancel}
      onRefresh={() => {
        void teaching
          .loadTeacherDashboard()
          .then(() =>
            homework.loadTeacherHomeworkOverview(
              teaching.snapshot().classes.map((item) => item.classId),
            ),
          )
          .catch(() => {
            if (teaching.snapshot().error !== null) {
              void homework.loadTeacherHomeworkOverview([]).catch(() => undefined);
            }
          });
      }}
    />
  );
}
